import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Plus, X, Columns3, Save, LayoutGrid, List as ListIcon, Users, SlidersHorizontal, GraduationCap } from "lucide-react";
import { toast } from "sonner";

function ListManager({ title, description, endpoint, dataKey, placeholder, icon: Icon, accent, onChanged, embedded }) {
  const { user } = useAuth();
  const canManage = hasPerm(user, "manage_settings");
  const [items, setItems] = useState(null);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get(endpoint).then((r) => setItems(r.data[dataKey] || [])).catch(() => setItems([])); }, [endpoint, dataKey]);

  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (items.some((x) => x.toLowerCase() === v.toLowerCase())) { toast.error("Already added"); return; }
    setItems([...items, v]);
    setInput("");
  };
  const remove = (v) => setItems(items.filter((x) => x !== v));
  const save = async () => {
    setSaving(true);
    try { const r = await api.put(endpoint, { items }); setItems(r.data[dataKey] || []); toast.success(`${title} saved`); if (onChanged) onChanged(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (items === null) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  return (
    <div className={embedded ? "space-y-4" : "space-y-6 max-w-2xl"} data-testid={`${dataKey}-page`}>
      {!embedded && (
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${accent}`}><Icon size={24} /></div>
          <div>
            <h1 className="font-heading text-3xl font-extrabold text-slate-900">{title}</h1>
            <p className="text-slate-500 text-sm">{description}</p>
          </div>
        </div>
      )}
      <div className={embedded ? "space-y-4" : "bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4"}>
        {canManage && (
          <div className="flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={placeholder} data-testid={`${dataKey}-input`} />
            <Button type="button" onClick={add} className="gap-1.5" data-testid={`${dataKey}-add-btn`}><Plus size={16} />Add</Button>
          </div>
        )}
        {items.length === 0 ? (
          <div className="py-10 text-center text-slate-400 text-sm">No {title.toLowerCase()} defined yet.</div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid={`${dataKey}-list`}>
            {items.map((v) => (
              <span key={v} className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-full bg-slate-100 text-slate-700 text-sm font-medium" data-testid={`${dataKey}-chip-${v}`}>
                {v}
                {canManage && <button type="button" onClick={() => remove(v)} className="text-slate-400 hover:text-rose-600" data-testid={`${dataKey}-remove-${v}`}><X size={14} /></button>}
              </span>
            ))}
          </div>
        )}
        {canManage && (
          <div className="flex justify-end pt-1">
            <Button onClick={save} disabled={saving} className="gap-1.5" data-testid={`${dataKey}-save-btn`}>{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}Save</Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function StreamsPage() {
  const { user } = useAuth();
  const canManage = hasPerm(user, "manage_settings");
  const [data, setData] = useState(null);
  const [streams, setStreams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [fClass, setFClass] = useState("all");
  const [fStream, setFStream] = useState("all");
  const [view, setView] = useState("table");
  const [manageOpen, setManageOpen] = useState(false);

  const load = () => api.get("/stream-stats").then((r) => setData(r.data)).catch(() => setData({ rows: [], total: 0 }));
  const loadStreams = () => api.get("/streams").then((r) => setStreams(r.data.streams || [])).catch(() => {});
  useEffect(() => { load(); loadStreams(); api.get("/classes").then((r) => setClasses(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  const classFiltered = data.rows.filter((r) => fClass === "all" || r.class_id === fClass);
  const rows = classFiltered.filter((r) => fStream === "all" || r.stream === fStream);
  const total = rows.reduce((a, r) => a + r.count, 0);
  const streamTotals = Object.values(classFiltered.reduce((m, r) => { (m[r.stream] = m[r.stream] || { stream: r.stream, count: 0 }).count += r.count; return m; }, {})).sort((a, b) => b.count - a.count);
  const classOpts = Object.values(data.rows.filter((r) => fStream === "all" || r.stream === fStream).reduce((m, r) => { m[r.class_id] = { id: r.class_id, name: r.class_name }; return m; }, {})).sort((a, b) => a.name.localeCompare(b.name));
  const onStream = (v) => { setFStream(v); if (v !== "all" && !data.rows.some((r) => r.stream === v && r.class_id === fClass)) setFClass("all"); };

  return (
    <div className="space-y-5" data-testid="streams-report-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-violet-100 text-violet-600 flex items-center justify-center"><Columns3 size={24} /></div>
          <div>
            <h1 className="font-heading text-3xl font-extrabold text-slate-900">Streams</h1>
            <p className="text-slate-500 text-sm">Student distribution across streams and classes.</p>
          </div>
        </div>
        {canManage && <Button variant="outline" className="gap-1.5" onClick={() => setManageOpen(true)} data-testid="manage-streams-btn"><SlidersHorizontal size={16} />Manage Streams</Button>}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3 max-w-xs shadow-sm" data-testid="streams-total-card">
        <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center"><Users size={20} /></div>
        <div><div className="text-2xl font-heading font-bold text-slate-900 leading-none" data-testid="streams-total-count">{total}</div><div className="text-xs text-slate-400 uppercase tracking-wide mt-1">Total Students</div></div>
      </div>

      {streamTotals.length > 0 && (
        <div data-testid="stream-totals">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Students by Stream</div>
          <div className="flex flex-wrap gap-2">
            {streamTotals.map((s) => (
              <button key={s.stream} type="button" onClick={() => onStream(fStream === s.stream ? "all" : s.stream)} className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-colors ${fStream === s.stream ? "border-violet-400 bg-violet-50" : "border-slate-200 bg-white hover:bg-slate-50"}`} data-testid={`stream-total-${s.stream}`}>
                <span className="font-medium text-slate-700">{s.stream}</span>
                <span className="inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full bg-violet-600 text-white text-xs font-semibold">{s.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Select value={fClass} onValueChange={setFClass}><SelectTrigger className="h-9 w-48" data-testid="stream-filter-class"><SelectValue placeholder="All Classes" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Classes</SelectItem>{classOpts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
        <Select value={fStream} onValueChange={onStream}><SelectTrigger className="h-9 w-48" data-testid="stream-filter-stream"><SelectValue placeholder="All Streams" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Streams</SelectItem>{streams.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden ml-auto">
          <button type="button" onClick={() => setView("grid")} className={`px-2.5 py-2 ${view === "grid" ? "bg-indigo-600 text-white" : "bg-white text-slate-500"}`} data-testid="stream-view-grid"><LayoutGrid size={16} /></button>
          <button type="button" onClick={() => setView("table")} className={`px-2.5 py-2 ${view === "table" ? "bg-indigo-600 text-white" : "bg-white text-slate-500"}`} data-testid="stream-view-table"><ListIcon size={16} /></button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 py-16 text-center text-slate-400" data-testid="streams-empty"><GraduationCap size={40} className="mx-auto mb-2 opacity-40" />No students match these filters. Assign a stream to students in the registration form.</div>
      ) : view === "table" ? (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="streams-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>{["Serial No.", "Stream Name", "Class", "Number of Students"].map((h) => <th key={h} className="px-4 py-3 text-left whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => (
                <tr key={`${r.class_id}-${r.stream}`} className="hover:bg-slate-50" data-testid={`stream-row-${i}`}>
                  <td className="px-4 py-3 text-slate-400">{i + 1}</td>
                  <td className="px-4 py-3"><span className="inline-flex px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-xs font-medium">{r.stream}</span></td>
                  <td className="px-4 py-3 text-slate-700">{r.class_name}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900 tabular">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="streams-grid">
          {rows.map((r, i) => (
            <div key={`${r.class_id}-${r.stream}`} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-shadow" data-testid={`stream-card-${i}`}>
              <div className="flex items-start justify-between">
                <span className="text-xs text-slate-400">#{i + 1}</span>
                <span className="inline-flex px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 text-xs font-medium">{r.stream}</span>
              </div>
              <div className="mt-2 text-lg font-heading font-bold text-slate-900">{r.class_name}</div>
              <div className="mt-3 flex items-center gap-1.5 text-slate-500 text-sm"><Users size={15} className="text-emerald-500" /><span className="font-semibold text-slate-800">{r.count}</span> students</div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={manageOpen} onOpenChange={(o) => { setManageOpen(o); if (!o) { loadStreams(); load(); } }}>
        <DialogContent className="max-w-lg" data-testid="manage-streams-dialog">
          <DialogHeader><DialogTitle>Manage Streams</DialogTitle><DialogDescription>Define the streams available for students (e.g. Science, Commerce, Arts).</DialogDescription></DialogHeader>
          <ListManager embedded title="Streams" endpoint="/streams" dataKey="streams" placeholder="e.g. Science" icon={Columns3} onChanged={() => { loadStreams(); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
